/**
 * CRMContacts.jsx
 * Super Admin — CRM > Contacts
 *
 * Features:
 *  - Paginated, searchable, filterable contacts table
 *  - Bulk select + bulk delete
 *  - Export CSV
 *  - Create / Edit contact via right-side drawer
 *  - Per-row: Edit, Delete, View Timeline, Send SMS, Schedule Demo
 *  - Timeline modal
 *  - Quick SMS modal
 *  - Lead status color badges
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  Plus, Search, Filter, Download, Pencil, Trash2, Clock, MessageSquare,
  Calendar, X, ChevronLeft, ChevronRight, AlertCircle, CheckSquare,
  Square, Users, RefreshCw,
} from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const authHeaders = () => ({
  Authorization: 'Bearer ' + localStorage.getItem('session_token'),
});

// ── Constants ────────────────────────────────────────────────────────────────

const INDIAN_STATES = [
  'Andhra Pradesh','Arunachal Pradesh','Assam','Bihar','Chhattisgarh','Goa',
  'Gujarat','Haryana','Himachal Pradesh','Jharkhand','Karnataka','Kerala',
  'Madhya Pradesh','Maharashtra','Manipur','Meghalaya','Mizoram','Nagaland',
  'Odisha','Punjab','Rajasthan','Sikkim','Tamil Nadu','Telangana','Tripura',
  'Uttar Pradesh','Uttarakhand','West Bengal','Delhi','Jammu and Kashmir',
  'Ladakh','Puducherry','Chandigarh',
];

const ORG_TYPES = ['College','University','Company','Training Institute'];

const LEAD_STATUSES = [
  'New','Contacted','Interested','Follow-up','Demo Requested',
  'Demo Scheduled','Demo Completed','Partner','Not Interested','Wrong Number',
];

const LEAD_STATUS_BADGE = {
  'New':            'bg-gray-100   text-gray-700',
  'Contacted':      'bg-blue-100   text-blue-700',
  'Interested':     'bg-green-100  text-green-700',
  'Follow-up':      'bg-yellow-100 text-yellow-700',
  'Demo Requested': 'bg-orange-100 text-orange-700',
  'Demo Scheduled': 'bg-purple-100 text-purple-700',
  'Demo Completed': 'bg-indigo-100 text-indigo-700',
  'Partner':        'bg-emerald-100 text-emerald-700',
  'Not Interested': 'bg-red-100    text-red-700',
  'Wrong Number':   'bg-slate-100  text-slate-600',
};

const EMPTY_FORM = {
  organization_name: '',
  organization_type: 'College',
  contact_person: '',
  designation: '',
  mobile_number: '',
  alternate_number: '',
  email: '',
  website: '',
  city: '',
  state: '',
  source: '',
  lead_status: 'New',
  assigned_to: '',
  notes: '',
};

// ── Helpers ───────────────────────────────────────────────────────────────────

const formatDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

const LeadBadge = ({ status }) => (
  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${LEAD_STATUS_BADGE[status] || 'bg-gray-100 text-gray-600'}`}>
    {status || 'Unknown'}
  </span>
);

// ── Form field helper ─────────────────────────────────────────────────────────

const Field = ({ label, required, children }) => (
  <div className="flex flex-col gap-1">
    <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
      {label}{required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
    {children}
  </div>
);

const inputCls = 'w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-purple-400';

// ── Main component ────────────────────────────────────────────────────────────

const CRMContacts = () => {
  const navigate = useNavigate();

  const [contacts,    setContacts]    = useState([]);
  const [total,       setTotal]       = useState(0);
  const [page,        setPage]        = useState(1);
  const [limit]                       = useState(20);
  const [search,      setSearch]      = useState('');
  const [filters,     setFilters]     = useState({ lead_status: '', organization_type: '', state: '' });
  const [sortBy,      setSortBy]      = useState('created_at');
  const [sortOrder,   setSortOrder]   = useState('DESC');
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState(null);

  // Drawer
  const [drawerOpen,     setDrawerOpen]     = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [formData,       setFormData]       = useState(EMPTY_FORM);
  const [formError,      setFormError]      = useState(null);
  const [formLoading,    setFormLoading]    = useState(false);

  // Bulk select
  const [selectedIds, setSelectedIds] = useState([]);

  // Timeline modal
  const [timelineModal, setTimelineModal] = useState({ open: false, contactId: null, events: [], loading: false, name: '' });

  // SMS modal
  const [smsModal,  setSmsModal]  = useState({ open: false, contact: null, message: '', loading: false });

  // Delete confirm
  const [deleteConfirm, setDeleteConfirm] = useState(null); // contact object

  // Search debounce
  const searchTimer = useRef(null);

  // ── Fetch contacts ──────────────────────────────────────────────────────────

  const fetchContacts = useCallback(async (pg = page) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page: pg, limit,
        search,
        lead_status:       filters.lead_status,
        organization_type: filters.organization_type,
        state:             filters.state,
        sort_by:           sortBy,
        sort_order:        sortOrder,
      });
      const res = await axios.get(`${API_BASE_URL}/api/crm/contacts?${params}`, {
        headers: authHeaders(),
      });
      const d = res.data?.data || res.data || {};
      setContacts(d.contacts || d.data || []);
      setTotal(d.total || 0);
    } catch (err) {
      console.error('[CRMContacts] fetch error:', err);
      const msg = err.response?.data?.message || err.message || 'Failed to load contacts.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [page, limit, search, filters, sortBy, sortOrder]);

  useEffect(() => {
    fetchContacts(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, filters, sortBy, sortOrder]);

  // Search with 400ms debounce
  const handleSearchChange = (val) => {
    setSearch(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setPage(1);
      fetchContacts(1);
    }, 400);
  };

  const handleFilterChange = (key, val) => {
    setFilters((f) => ({ ...f, [key]: val }));
    setPage(1);
  };

  const handleSort = (col) => {
    if (sortBy === col) {
      setSortOrder((o) => (o === 'ASC' ? 'DESC' : 'ASC'));
    } else {
      setSortBy(col);
      setSortOrder('DESC');
    }
  };

  // ── Drawer helpers ──────────────────────────────────────────────────────────

  const openCreate = () => {
    setEditingContact(null);
    setFormData(EMPTY_FORM);
    setFormError(null);
    setDrawerOpen(true);
  };

  const openEdit = (contact) => {
    setEditingContact(contact);
    setFormData({
      organization_name: contact.organization_name || '',
      organization_type: contact.organization_type || 'College',
      contact_person:    contact.contact_person    || '',
      designation:       contact.designation       || '',
      mobile_number:     contact.mobile_number     || '',
      alternate_number:  contact.alternate_number  || '',
      email:             contact.email             || '',
      website:           contact.website           || '',
      city:              contact.city              || '',
      state:             contact.state             || '',
      source:            contact.source            || '',
      lead_status:       contact.lead_status       || 'New',
      assigned_to:       contact.assigned_to       || '',
      notes:             contact.notes             || '',
    });
    setFormError(null);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditingContact(null);
    setFormData(EMPTY_FORM);
    setFormError(null);
  };

  const handleFormChange = (key, val) => setFormData((f) => ({ ...f, [key]: val }));

  const validateForm = () => {
    if (!formData.organization_name.trim()) return 'Organization name is required.';
    if (!formData.organization_type)        return 'Organization type is required.';
    if (!formData.mobile_number.trim())     return 'Mobile number is required.';
    if (!/^\d{10}$/.test(formData.mobile_number.trim())) return 'Mobile number must be exactly 10 digits.';
    return null;
  };

  const handleSubmitForm = async (e) => {
    e.preventDefault();
    const err = validateForm();
    if (err) { setFormError(err); return; }
    setFormLoading(true);
    setFormError(null);
    try {
      if (editingContact) {
        await axios.put(`${API_BASE_URL}/api/crm/contacts/${editingContact.id}`, formData, {
          headers: authHeaders(),
        });
      } else {
        await axios.post(`${API_BASE_URL}/api/crm/contacts`, formData, {
          headers: authHeaders(),
        });
      }
      closeDrawer();
      fetchContacts(page);
    } catch (err) {
      setFormError(err.response?.data?.message || 'Failed to save contact.');
    } finally {
      setFormLoading(false);
    }
  };

  // ── Delete ──────────────────────────────────────────────────────────────────

  const handleDelete = async (contact) => {
    try {
      await axios.delete(`${API_BASE_URL}/api/crm/contacts/${contact.id}`, {
        headers: authHeaders(),
      });
      setDeleteConfirm(null);
      fetchContacts(page);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete contact.');
    }
  };

  // ── Bulk delete ─────────────────────────────────────────────────────────────

  const handleBulkDelete = async () => {
    if (!selectedIds.length) return;
    if (!window.confirm(`Delete ${selectedIds.length} selected contact(s)? This cannot be undone.`)) return;
    try {
      await axios.post(`${API_BASE_URL}/api/crm/contacts/bulk-delete`, { ids: selectedIds }, {
        headers: authHeaders(),
      });
      setSelectedIds([]);
      fetchContacts(page);
    } catch (err) {
      alert(err.response?.data?.message || 'Bulk delete failed.');
    }
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === contacts.length && contacts.length > 0) {
      setSelectedIds([]);
    } else {
      setSelectedIds(contacts.map((c) => c.id));
    }
  };

  const toggleSelectOne = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  // ── Export CSV ──────────────────────────────────────────────────────────────

  const handleExport = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/crm/contacts/export`, {
        headers: authHeaders(),
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `crm-contacts-${new Date().toISOString().slice(0,10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert('Export failed. Please try again.');
    }
  };

  // ── Timeline modal ──────────────────────────────────────────────────────────

  const openTimeline = async (contact) => {
    setTimelineModal({ open: true, contactId: contact.id, events: [], loading: true, name: contact.organization_name });
    try {
      const res = await axios.get(`${API_BASE_URL}/api/crm/contacts/${contact.id}/timeline`, {
        headers: authHeaders(),
      });
      const events = res.data?.data?.timeline || res.data?.timeline || res.data?.data || res.data?.events || res.data || [];
      setTimelineModal((t) => ({ ...t, events, loading: false }));
    } catch (err) {
      console.error('[CRMContacts] timeline error:', err);
      setTimelineModal((t) => ({ ...t, events: [], loading: false }));
    }
  };

  const closeTimeline = () =>
    setTimelineModal({ open: false, contactId: null, events: [], loading: false, name: '' });

  // ── SMS modal ───────────────────────────────────────────────────────────────

  const openSMS = (contact) => setSmsModal({ open: true, contact, message: '', loading: false });

  const handleSendSMS = async () => {
    if (!smsModal.message.trim()) return;
    setSmsModal((m) => ({ ...m, loading: true }));
    try {
      await axios.post(`${API_BASE_URL}/api/crm/sms/send`, {
        contact_id: smsModal.contact.id,
        mobile: smsModal.contact.mobile_number,
        message: smsModal.message.trim(),
      }, { headers: authHeaders() });
      setSmsModal({ open: false, contact: null, message: '', loading: false });
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to send SMS.');
      setSmsModal((m) => ({ ...m, loading: false }));
    }
  };

  // ── Pagination ──────────────────────────────────────────────────────────────

  const totalPages = Math.ceil(total / limit) || 1;
  const from = total === 0 ? 0 : (page - 1) * limit + 1;
  const to   = Math.min(page * limit, total);

  const SortArrow = ({ col }) => {
    if (sortBy !== col) return <span className="ml-1 text-gray-300">↕</span>;
    return <span className="ml-1 text-purple-600">{sortOrder === 'ASC' ? '↑' : '↓'}</span>;
  };

  const thCls = 'px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap cursor-pointer select-none hover:text-gray-800 transition-colors';

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-gray-100 p-6">

      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-purple-100 border border-purple-200 flex items-center justify-center">
            <Users size={18} className="text-[#843FEF]" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900 tracking-tight">CRM Contacts</h1>
            <p className="text-xs text-gray-400">{total.toLocaleString('en-IN')} total contacts</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {selectedIds.length > 0 && (
            <button
              onClick={handleBulkDelete}
              className="flex items-center gap-1.5 px-3 py-2 bg-red-50 border border-red-200 text-red-600 text-sm font-semibold rounded-lg hover:bg-red-100 transition-colors"
            >
              <Trash2 size={14} />
              Delete ({selectedIds.length})
            </button>
          )}
          <button
            onClick={handleExport}
            className="flex items-center gap-1.5 px-3 py-2 bg-white border border-gray-200 text-gray-700 text-sm font-semibold rounded-lg hover:bg-gray-50 transition-colors shadow-sm"
          >
            <Download size={14} />
            Export CSV
          </button>
          <button
            onClick={openCreate}
            className="flex items-center gap-1.5 px-4 py-2 bg-[#843FEF] text-white text-sm font-semibold rounded-lg hover:bg-purple-700 transition-colors shadow-sm"
          >
            <Plus size={15} />
            Create Contact
          </button>
        </div>
      </div>

      {/* Filters bar */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-4 py-3 mb-5 flex flex-wrap gap-3 items-center">
        {/* Search */}
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder="Search org name, person, mobile..."
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-200"
          />
        </div>

        {/* Lead Status */}
        <div className="relative">
          <Filter size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <select
            value={filters.lead_status}
            onChange={(e) => handleFilterChange('lead_status', e.target.value)}
            className="pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-200 appearance-none"
          >
            <option value="">All Statuses</option>
            {LEAD_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>

        {/* Org Type */}
        <div className="relative">
          <Filter size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <select
            value={filters.organization_type}
            onChange={(e) => handleFilterChange('organization_type', e.target.value)}
            className="pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-200 appearance-none"
          >
            <option value="">All Types</option>
            {ORG_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>

        {/* State */}
        <div className="relative">
          <Filter size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <select
            value={filters.state}
            onChange={(e) => handleFilterChange('state', e.target.value)}
            className="pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-200 appearance-none"
          >
            <option value="">All States</option>
            {INDIAN_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>

        {(filters.lead_status || filters.organization_type || filters.state || search) && (
          <button
            onClick={() => {
              setFilters({ lead_status: '', organization_type: '', state: '' });
              setSearch('');
              setPage(1);
            }}
            className="text-xs text-gray-400 hover:text-gray-700 font-medium transition-colors"
          >
            Clear filters
          </button>
        )}

        <button
          onClick={() => fetchContacts(page)}
          className="ml-auto text-gray-400 hover:text-gray-700 transition-colors"
          title="Reload"
        >
          <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 mb-4 text-sm">
          <AlertCircle size={15} className="flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {/* Checkbox all */}
                <th className="px-4 py-3 w-10">
                  <button onClick={toggleSelectAll} className="text-gray-400 hover:text-purple-600 transition-colors">
                    {selectedIds.length === contacts.length && contacts.length > 0
                      ? <CheckSquare size={16} className="text-purple-600" />
                      : <Square size={16} />}
                  </button>
                </th>
                <th className={thCls} onClick={() => handleSort('organization_name')}>
                  Org Name <SortArrow col="organization_name" />
                </th>
                <th className={thCls} onClick={() => handleSort('organization_type')}>
                  Type <SortArrow col="organization_type" />
                </th>
                <th className={thCls} onClick={() => handleSort('contact_person')}>
                  Contact Person <SortArrow col="contact_person" />
                </th>
                <th className={thCls}>Mobile</th>
                <th className={thCls} onClick={() => handleSort('state')}>
                  State <SortArrow col="state" />
                </th>
                <th className={thCls} onClick={() => handleSort('lead_status')}>
                  Lead Status <SortArrow col="lead_status" />
                </th>
                <th className={thCls} onClick={() => handleSort('created_at')}>
                  Created <SortArrow col="created_at" />
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={9} className="py-16 text-center">
                    <RefreshCw size={24} className="animate-spin text-purple-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-400">Loading contacts...</p>
                  </td>
                </tr>
              ) : contacts.length === 0 ? (
                <tr>
                  <td colSpan={9} className="py-16 text-center">
                    <Users size={36} className="mx-auto mb-3 text-gray-200" />
                    <p className="text-sm font-semibold text-gray-500">No contacts found</p>
                    <p className="text-xs text-gray-400 mt-1">Try adjusting your filters or create a new contact.</p>
                  </td>
                </tr>
              ) : (
                contacts.map((contact) => (
                  <tr
                    key={contact.id}
                    className={`hover:bg-gray-50 transition-colors ${selectedIds.includes(contact.id) ? 'bg-purple-50' : ''}`}
                  >
                    {/* Checkbox */}
                    <td className="px-4 py-3">
                      <button
                        onClick={() => toggleSelectOne(contact.id)}
                        className="text-gray-400 hover:text-purple-600 transition-colors"
                      >
                        {selectedIds.includes(contact.id)
                          ? <CheckSquare size={15} className="text-purple-600" />
                          : <Square size={15} />}
                      </button>
                    </td>

                    <td className="px-4 py-3">
                      <p className="text-sm font-semibold text-gray-900 max-w-[160px] truncate">
                        {contact.organization_name || '—'}
                      </p>
                    </td>

                    <td className="px-4 py-3">
                      <span className="text-xs text-gray-500 font-medium">{contact.organization_type || '—'}</span>
                    </td>

                    <td className="px-4 py-3">
                      <p className="text-sm text-gray-800">{contact.contact_person || '—'}</p>
                      {contact.designation && (
                        <p className="text-xs text-gray-400">{contact.designation}</p>
                      )}
                    </td>

                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-700 font-mono">{contact.mobile_number || '—'}</span>
                    </td>

                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-600">{contact.state || '—'}</span>
                    </td>

                    <td className="px-4 py-3">
                      <LeadBadge status={contact.lead_status} />
                    </td>

                    <td className="px-4 py-3">
                      <span className="text-xs text-gray-400">{formatDate(contact.created_at)}</span>
                    </td>

                    {/* Actions */}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        {/* Edit */}
                        <ActionBtn
                          title="Edit"
                          icon={<Pencil size={13} />}
                          colorCls="hover:bg-blue-50 hover:text-blue-600"
                          onClick={() => openEdit(contact)}
                        />
                        {/* Delete */}
                        <ActionBtn
                          title="Delete"
                          icon={<Trash2 size={13} />}
                          colorCls="hover:bg-red-50 hover:text-red-600"
                          onClick={() => setDeleteConfirm(contact)}
                        />
                        {/* Timeline */}
                        <ActionBtn
                          title="View Timeline"
                          icon={<Clock size={13} />}
                          colorCls="hover:bg-yellow-50 hover:text-yellow-600"
                          onClick={() => openTimeline(contact)}
                        />
                        {/* SMS */}
                        <ActionBtn
                          title="Send SMS"
                          icon={<MessageSquare size={13} />}
                          colorCls="hover:bg-green-50 hover:text-green-600"
                          onClick={() => openSMS(contact)}
                        />
                        {/* Schedule Demo */}
                        <ActionBtn
                          title="Schedule Demo"
                          icon={<Calendar size={13} />}
                          colorCls="hover:bg-purple-50 hover:text-purple-600"
                          onClick={() => navigate('/superadmin/crm/demos', { state: { contact } })}
                        />
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {total > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
            <p className="text-xs text-gray-500">
              Showing {from}–{to} of {total.toLocaleString('en-IN')}
            </p>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-1.5 rounded-lg border border-gray-200 text-gray-500 disabled:opacity-40 hover:bg-gray-50 transition-colors"
              >
                <ChevronLeft size={14} />
              </button>
              <span className="px-3 py-1 text-xs font-semibold text-gray-700">
                {page} / {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="p-1.5 rounded-lg border border-gray-200 text-gray-500 disabled:opacity-40 hover:bg-gray-50 transition-colors"
              >
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Create/Edit Drawer ──────────────────────────────────────────────── */}
      {drawerOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/20 z-40"
            onClick={closeDrawer}
          />
          {/* Panel */}
          <aside className="fixed top-0 right-0 bottom-0 w-96 bg-white shadow-2xl z-50 flex flex-col">
            {/* Drawer header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <h2 className="text-base font-bold text-gray-900">
                {editingContact ? 'Edit Contact' : 'Create Contact'}
              </h2>
              <button onClick={closeDrawer} className="text-gray-400 hover:text-gray-700 transition-colors">
                <X size={18} />
              </button>
            </div>

            {/* Form body */}
            <form onSubmit={handleSubmitForm} className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
              {formError && (
                <div className="bg-red-50 border border-red-200 text-red-700 text-xs rounded-lg px-3 py-2 flex items-center gap-2">
                  <AlertCircle size={13} className="flex-shrink-0" />
                  {formError}
                </div>
              )}

              <Field label="Organization Name" required>
                <input
                  type="text"
                  className={inputCls}
                  value={formData.organization_name}
                  onChange={(e) => handleFormChange('organization_name', e.target.value)}
                  placeholder="e.g. ABC College of Engineering"
                />
              </Field>

              <Field label="Organization Type" required>
                <select
                  className={inputCls}
                  value={formData.organization_type}
                  onChange={(e) => handleFormChange('organization_type', e.target.value)}
                >
                  {ORG_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </Field>

              <div className="grid grid-cols-2 gap-3">
                <Field label="Contact Person">
                  <input
                    type="text"
                    className={inputCls}
                    value={formData.contact_person}
                    onChange={(e) => handleFormChange('contact_person', e.target.value)}
                    placeholder="Full name"
                  />
                </Field>
                <Field label="Designation">
                  <input
                    type="text"
                    className={inputCls}
                    value={formData.designation}
                    onChange={(e) => handleFormChange('designation', e.target.value)}
                    placeholder="e.g. Principal"
                  />
                </Field>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <Field label="Mobile Number" required>
                  <input
                    type="tel"
                    maxLength={10}
                    className={inputCls}
                    value={formData.mobile_number}
                    onChange={(e) => handleFormChange('mobile_number', e.target.value.replace(/\D/g, ''))}
                    placeholder="10-digit number"
                  />
                </Field>
                <Field label="Alternate Number">
                  <input
                    type="tel"
                    maxLength={10}
                    className={inputCls}
                    value={formData.alternate_number}
                    onChange={(e) => handleFormChange('alternate_number', e.target.value.replace(/\D/g, ''))}
                    placeholder="Optional"
                  />
                </Field>
              </div>

              <Field label="Email">
                <input
                  type="email"
                  className={inputCls}
                  value={formData.email}
                  onChange={(e) => handleFormChange('email', e.target.value)}
                  placeholder="contact@example.com"
                />
              </Field>

              <Field label="Website">
                <input
                  type="url"
                  className={inputCls}
                  value={formData.website}
                  onChange={(e) => handleFormChange('website', e.target.value)}
                  placeholder="https://"
                />
              </Field>

              <div className="grid grid-cols-2 gap-3">
                <Field label="City">
                  <input
                    type="text"
                    className={inputCls}
                    value={formData.city}
                    onChange={(e) => handleFormChange('city', e.target.value)}
                    placeholder="City"
                  />
                </Field>
                <Field label="State">
                  <select
                    className={inputCls}
                    value={formData.state}
                    onChange={(e) => handleFormChange('state', e.target.value)}
                  >
                    <option value="">Select state</option>
                    {INDIAN_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </Field>
              </div>

              <Field label="Source">
                <input
                  type="text"
                  className={inputCls}
                  value={formData.source}
                  onChange={(e) => handleFormChange('source', e.target.value)}
                  placeholder="e.g. LinkedIn, Referral"
                />
              </Field>

              <Field label="Lead Status">
                <select
                  className={inputCls}
                  value={formData.lead_status}
                  onChange={(e) => handleFormChange('lead_status', e.target.value)}
                >
                  {LEAD_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>

              <Field label="Assigned To">
                <input
                  type="text"
                  className={inputCls}
                  value={formData.assigned_to}
                  onChange={(e) => handleFormChange('assigned_to', e.target.value)}
                  placeholder="Team member name"
                />
              </Field>

              <Field label="Notes">
                <textarea
                  rows={3}
                  className={inputCls + ' resize-none'}
                  value={formData.notes}
                  onChange={(e) => handleFormChange('notes', e.target.value)}
                  placeholder="Any relevant notes..."
                />
              </Field>
            </form>

            {/* Drawer footer */}
            <div className="px-5 py-4 border-t border-gray-100 flex gap-3">
              <button
                type="button"
                onClick={closeDrawer}
                className="flex-1 py-2.5 rounded-lg border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitForm}
                disabled={formLoading}
                className="flex-[2] py-2.5 rounded-lg bg-[#843FEF] text-white text-sm font-semibold hover:bg-purple-700 disabled:opacity-60 transition-colors flex items-center justify-center gap-2"
              >
                {formLoading
                  ? <><RefreshCw size={13} className="animate-spin" /> Saving...</>
                  : editingContact ? 'Save Changes' : 'Create Contact'}
              </button>
            </div>
          </aside>
        </>
      )}

      {/* ── Delete Confirm Dialog ──────────────────────────────────────────── */}
      {deleteConfirm && (
        <ModalOverlay onClose={() => setDeleteConfirm(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
            <div className="w-11 h-11 rounded-xl bg-red-100 flex items-center justify-center mb-4">
              <Trash2 size={20} className="text-red-600" />
            </div>
            <h3 className="text-base font-bold text-gray-900 mb-1">Delete Contact?</h3>
            <p className="text-sm text-gray-500 mb-5">
              This will permanently delete <strong>{deleteConfirm.organization_name}</strong>. This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                className="flex-[2] py-2.5 rounded-xl bg-red-600 text-white text-sm font-semibold hover:bg-red-700 transition-colors"
              >
                Yes, Delete
              </button>
            </div>
          </div>
        </ModalOverlay>
      )}

      {/* ── Timeline Modal ─────────────────────────────────────────────────── */}
      {timelineModal.open && (
        <ModalOverlay onClose={closeTimeline}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <div>
                <h3 className="text-base font-bold text-gray-900">Timeline</h3>
                <p className="text-xs text-gray-400">{timelineModal.name}</p>
              </div>
              <button onClick={closeTimeline} className="text-gray-400 hover:text-gray-700 transition-colors">
                <X size={18} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-4">
              {timelineModal.loading ? (
                <div className="py-12 flex flex-col items-center gap-2 text-gray-400">
                  <RefreshCw size={22} className="animate-spin" />
                  <p className="text-sm">Loading timeline...</p>
                </div>
              ) : timelineModal.events.length === 0 ? (
                <div className="py-12 text-center text-gray-400">
                  <Clock size={32} className="mx-auto mb-2 opacity-30" />
                  <p className="text-sm">No timeline events yet.</p>
                </div>
              ) : (
                <ol className="relative border-l-2 border-purple-100 pl-5 space-y-5">
                  {timelineModal.events.map((ev, i) => (
                    <li key={ev.id || i} className="relative">
                      <span className="absolute -left-[23px] top-0.5 w-4 h-4 bg-purple-100 border-2 border-purple-300 rounded-full" />
                      <p className="text-sm font-semibold text-gray-800">{ev.action || ev.event_type || ev.title || 'Event'}</p>
                      {ev.description && (
                        <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{ev.description}</p>
                      )}
                      <p className="text-xs text-gray-400 mt-1">
                        {ev.performed_by || ev.created_by || ''}{ev.created_at ? ` · ${formatDate(ev.created_at)}` : ''}
                      </p>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </div>
        </ModalOverlay>
      )}

      {/* ── SMS Modal ──────────────────────────────────────────────────────── */}
      {smsModal.open && (
        <ModalOverlay onClose={() => setSmsModal({ open: false, contact: null, message: '', loading: false })}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-bold text-gray-900">Send SMS</h3>
              <button
                onClick={() => setSmsModal({ open: false, contact: null, message: '', loading: false })}
                className="text-gray-400 hover:text-gray-700 transition-colors"
              >
                <X size={18} />
              </button>
            </div>
            <p className="text-sm text-gray-500 mb-4">
              To: <strong>{smsModal.contact?.contact_person || smsModal.contact?.organization_name}</strong>
              {' '}({smsModal.contact?.mobile_number})
            </p>
            <textarea
              rows={4}
              className={inputCls + ' resize-none mb-4'}
              placeholder="Type your SMS message..."
              value={smsModal.message}
              onChange={(e) => setSmsModal((m) => ({ ...m, message: e.target.value }))}
            />
            <div className="flex gap-3">
              <button
                onClick={() => setSmsModal({ open: false, contact: null, message: '', loading: false })}
                className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSendSMS}
                disabled={smsModal.loading || !smsModal.message.trim()}
                className="flex-[2] py-2.5 rounded-xl bg-green-600 text-white text-sm font-semibold hover:bg-green-700 disabled:opacity-60 transition-colors flex items-center justify-center gap-2"
              >
                {smsModal.loading
                  ? <><RefreshCw size={13} className="animate-spin" /> Sending...</>
                  : <><MessageSquare size={13} /> Send SMS</>}
              </button>
            </div>
          </div>
        </ModalOverlay>
      )}
    </div>
  );
};

// ── Shared modal overlay ──────────────────────────────────────────────────────

const ModalOverlay = ({ children, onClose }) => (
  <div
    className="fixed inset-0 z-50 bg-black/30 backdrop-blur-sm flex items-center justify-center p-4"
    onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
  >
    {children}
  </div>
);

// ── Icon action button ────────────────────────────────────────────────────────

const ActionBtn = ({ icon, title, colorCls, onClick }) => (
  <button
    title={title}
    onClick={onClick}
    className={`p-1.5 rounded-lg text-gray-400 transition-colors ${colorCls}`}
  >
    {icon}
  </button>
);

export default CRMContacts;
